import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { type TemplateSummary, useApplyTemplate } from '@/entities/template';
import { useWorkspaces, type Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, DialogActions, TextField, useToast } from '@/shared/ui';

import styles from './Templates.module.css';

type Creatable = 'board' | 'database' | 'document';

const CREATE: Record<Creatable, { permission: string; route: string }> = {
  board: { permission: 'workspace.create_board', route: '/app/boards' },
  database: { permission: 'workspace.create_database', route: '/app/databases' },
  document: { permission: 'workspace.create_document', route: '/app/documents' },
};

/**
 * "Create from template" in Settings: choose where (only workspaces where the viewer may create
 * this kind of resource) and an optional name. The server checks the right again.
 */
export function UseTemplateDialog({
  template,
  kind,
  onClose,
}: {
  template: TemplateSummary;
  kind: Creatable;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const workspaces = useWorkspaces();
  const apply = useApplyTemplate();
  const where = (workspaces.data ?? []).filter((w: Workspace) =>
    w.permissions.includes(CREATE[kind].permission),
  );
  const [chosen, setChosen] = useState<string | null>(null);
  const [name, setName] = useState('');
  const workspaceId = chosen ?? where[0]?.id ?? null;
  return (
    <Dialog
      title={t('templates.manage.useTitle', { name: template.name })}
      onClose={onClose}
      footer={
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="template-use-form"
            variant="primary"
            disabled={apply.isPending || workspaceId === null}
          >
            {t('templates.manage.use')}
          </Button>
        </DialogActions>
      }
    >
      <form
        id="template-use-form"
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (workspaceId === null || apply.isPending) return;
          apply.mutate(
            {
              kind,
              templateId: template.id,
              workspaceId,
              ...(name.trim() && { name: name.trim() }),
            },
            {
              onSuccess: (made) => {
                toast.show({
                  message: t('templates.manage.created', { name: name.trim() || template.name }),
                });
                onClose();
                void navigate(`${CREATE[kind].route}/${made.resourceId}`);
              },
            },
          );
        }}
      >
        {where.length === 0 && !workspaces.isPending ? (
          <p className={styles.note}>{t('templates.manage.noWorkspace')}</p>
        ) : (
          <Combobox
            label={t('templates.manage.workspace')}
            value={workspaceId ?? ''}
            options={where.map((w) => ({ value: w.id, label: w.name }))}
            onChange={setChosen}
          />
        )}
        <TextField
          label={t('templates.name')}
          value={name}
          maxLength={100}
          placeholder={template.headline || template.name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <FormError error={apply.error} />
      </form>
    </Dialog>
  );
}
